import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { IconArrow, IconPlay } from "./SiteIcons";

/** Arrow used by every button and text link. Colour follows the text (currentColor). */
export const SiteArrow = () => <IconArrow className="sm_btn_icon sm_btn_icon--arrow" />;

/**
 * Opens an in-site path. A path with a hash (/#how-it-works) scrolls to that section once the page
 * has rendered; any other path opens at the top.
 */
export const useSiteNavigate = () => {
	const navigate = useNavigate();

	return (to) => {
		const [path, hash] = to.split("#");
		navigate(path || "/");
		setTimeout(() => {
			const target = hash && document.getElementById(hash);
			if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
			else window.scrollTo({ top: 0 });
		}, 60);
	};
};

/**
 * Website button.
 * variant: "primary" (orange), "outline" (navy outline) or "light" (white, on the navy bands).
 * icon: "arrow" (default), "play" (blue play badge before the label) or false.
 * Pass `to` for an in-site link, `href` for an external link, or `onClick`.
 */
const SiteButton = ({ to, state, href, onClick, variant = "primary", size, block = false, icon = "arrow", type = "button", className = "", children, ...rest }) => {
	const go = useSiteNavigate();
	const classes = ["sm_btn", `sm_btn--${variant}`, size ? `sm_btn--${size}` : "", block ? "sm_btn--block" : "", className].filter(Boolean).join(" ");
	const content = (
		<>
			{icon === "play" && (
				<span className="sm_btn_play" aria-hidden="true">
					<IconPlay />
				</span>
			)}
			<span>{children}</span>
			{icon === "arrow" && <SiteArrow />}
		</>
	);

	if (to) {
		const handleClick = (event) => {
			if (onClick) onClick(event);
			// Keep new-tab and modified clicks working as normal links.
			if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
			if (state) {
				// The Link carries the state; just open the page at the top.
				window.scrollTo({ top: 0 });
				return;
			}
			event.preventDefault();
			go(to);
		};
		return (
			<Link to={to} state={state} className={classes} onClick={handleClick} {...rest}>
				{content}
			</Link>
		);
	}

	if (href) {
		return (
			<a href={href} className={classes} onClick={onClick} target="_blank" rel="noopener noreferrer" {...rest}>
				{content}
			</a>
		);
	}

	return (
		<button type={type} className={classes} onClick={onClick} {...rest}>
			{content}
		</button>
	);
};

export default SiteButton;
